/** @file Viewport toolbar: one button per tool, Setup/Animate, Snap, overlay toggles and frame selection. */
import type { CatalogKey, Translate } from '@pose2d/i18n/types';
import { useStore } from 'zustand';
import { toolStore } from '../../stores/toolStore';
import type { ToolDef } from '../../stores/types';
import { TOOLS } from '../../tools.gen';
import type { OverlayToggles } from './scene/overlayOptionsFor';
import { viewportApi } from './viewportApi';
import { viewportStore } from './viewportStore';

const TOGGLES: readonly (readonly [keyof OverlayToggles, CatalogKey])[] = [
  ['bones', 'viewport.bones'], ['names', 'viewport.names'], ['mesh', 'viewport.mesh'], ['ik', 'viewport.ik'],
  ['sockets', 'viewport.sockets'], ['axes', 'viewport.axes'], ['grid', 'viewport.grid'], ['onion', 'viewport.onion'],
];

/** aria-pressed carries every on/off state; labels are translated, chords are listed in Settings. */
export function ViewportToolbar({ t }: { t: Translate }) {
  const tool = useStore(toolStore, (s) => s);
  const toggles = useStore(viewportStore, (s) => s.toggles);
  const tools: readonly ToolDef[] = TOOLS;
  const flip = (key: keyof OverlayToggles): void => { viewportStore.setState({ toggles: { ...toggles, [key]: !toggles[key] } }); };
  return (
    <div className="viewport-toolbar" role="toolbar" aria-label={t('viewport.toolbar')}>
      {tools.map((d) => <button key={d.id} type="button" aria-pressed={tool.active === d.id} onClick={() => { toolStore.setState({ active: d.id }); }}>{t(d.title)}</button>)}
      <button type="button" aria-pressed={tool.mode === 'animate'} onClick={() => { toolStore.setState({ mode: tool.mode === 'setup' ? 'animate' : 'setup' }); }}>{t(tool.mode === 'setup' ? 'viewport.modeSetup' : 'viewport.modeAnimate')}</button>
      <button type="button" aria-pressed={tool.snap} onClick={() => { toolStore.setState({ snap: !tool.snap }); }}>{t('viewport.snap')}</button>
      {TOGGLES.map(([key, label]) => <button key={key} type="button" aria-pressed={toggles[key]} onClick={() => { flip(key); }}>{t(label)}</button>)}
      <button type="button" onClick={() => { viewportApi.frameSelection(); }}>{t('viewport.frameSelection')}</button>
    </div>
  );
}
